import { contactDisplayName, type Contact } from '../../../types';

type PartyContact = Pick<
  Contact,
  'id' | 'contact_type' | 'contact_kind' | 'company' | 'organization_id' | 'first_name' | 'last_name' | 'name'
>;

/** Gewerblicher Kunde (Legacy) oder Gewerblicher Kontakt (aktuell) am Lead. */
export function isGewerblicherKundeContact(contact: PartyContact | null | undefined): boolean {
  if (!contact) return false;
  if (contact.contact_type === 'Gewerblicher Kunde') return true;
  if (contact.contact_kind === 'Gewerblicher Kontakt') return true;
  return false;
}

export function commercialCustomerCompanyName(
  contact: PartyContact,
  organizationNameById: ReadonlyMap<string, string>,
): string {
  const orgId = contact.organization_id != null ? String(contact.organization_id).trim() : '';
  if (orgId) {
    const fromOrg = (organizationNameById.get(orgId) ?? '').trim();
    if (fromOrg) return fromOrg;
  }
  return (contact.company ?? '').trim();
}

export type InvoicePartyLead = {
  name?: string | null;
  contact_id?: string | number | null;
  contact?: PartyContact | PartyContact[] | null;
};

export type InvoicePartyDisplayContext = {
  leadsById: ReadonlyMap<string, InvoicePartyLead>;
  contacts: readonly PartyContact[];
  organizationNameById: ReadonlyMap<string, string>;
};

function findContactForPartyName(
  partyName: string,
  contacts: readonly PartyContact[],
  mode: 'exact' | 'fuzzy',
): PartyContact | undefined {
  const needle = partyName.trim().toLowerCase();
  if (!needle) return undefined;
  return contacts.find((c) => {
    const candidates = [
      contactDisplayName(c),
      (c.name ?? '').trim(),
      commercialCustomerCompanyName(c, new Map()),
    ]
      .map((v) => v.toLowerCase())
      .filter(Boolean);
    return candidates.some((v) => {
      if (v === needle) return true;
      if (mode === 'fuzzy') return v.includes(needle) || needle.includes(v);
      return false;
    });
  });
}

function resolvePartyContact(
  partyName: string | null | undefined,
  leadId: string | null | undefined,
  ctx: InvoicePartyDisplayContext,
  nameMatch: 'exact' | 'fuzzy',
): { contact: PartyContact | null; leadContactId: string | null } {
  const stored = (partyName ?? '').trim();
  let contact: PartyContact | null | undefined;
  let leadContactId: string | null = null;

  if (leadId) {
    const lead = ctx.leadsById.get(String(leadId));
    const raw = lead?.contact;
    contact = raw == null ? null : Array.isArray(raw) ? raw[0] : raw;
    if (lead?.contact_id != null && String(lead.contact_id).trim()) {
      leadContactId = String(lead.contact_id);
    }
  }

  if (contact?.id != null) {
    const fromList = ctx.contacts.find((c) => String(c.id) === String(contact?.id));
    if (fromList) contact = { ...contact, ...fromList };
  } else if (leadContactId) {
    const fromList = ctx.contacts.find((c) => String(c.id) === leadContactId);
    if (fromList) contact = fromList;
  }

  if (!contact && stored) {
    contact = findContactForPartyName(stored, ctx.contacts, nameMatch) ?? null;
  }

  return { contact: contact ?? null, leadContactId };
}

/** Anzeige in Kunden-/Lieferantenspalte: Firma bei Gewerblichem Kunden, sonst Kontaktname. */
export function invoicePartyColumnLabel(
  partyName: string | null | undefined,
  leadId: string | null | undefined,
  ctx: InvoicePartyDisplayContext,
  organizationId?: string | null,
): string {
  const stored = (partyName ?? '').trim();
  const orgId = String(organizationId ?? '').trim();
  if (orgId) {
    const fromOrg = (ctx.organizationNameById.get(orgId) ?? '').trim();
    if (fromOrg) return fromOrg;
    if (stored) return stored;
  }
  const { contact } = resolvePartyContact(partyName, leadId, ctx, 'fuzzy');

  if (contact && isGewerblicherKundeContact(contact)) {
    const company = commercialCustomerCompanyName(contact, ctx.organizationNameById);
    if (company) return company;
  }

  if (contact) {
    const person = contactDisplayName(contact);
    if (person) return person;
  }

  if (leadId) {
    const leadName = (ctx.leadsById.get(String(leadId))?.name ?? '').trim();
    if (leadName) return leadName;
  }

  return stored;
}

export type InvoicePartyTarget =
  | { kind: 'organization'; id: string }
  | { kind: 'contact'; id: string };

/** Ziel für den klickbaren Kundennamen: Organisation bei Gewerblich, sonst Kontakt. */
export function invoicePartyTarget(
  partyName: string | null | undefined,
  leadId: string | null | undefined,
  ctx: InvoicePartyDisplayContext,
  organizationId?: string | null,
): InvoicePartyTarget | null {
  const orgId = String(organizationId ?? '').trim();
  if (orgId) return { kind: 'organization', id: orgId };
  const { contact, leadContactId } = resolvePartyContact(partyName, leadId, ctx, 'exact');

  if (contact && isGewerblicherKundeContact(contact)) {
    const orgId = contact.organization_id != null ? String(contact.organization_id).trim() : '';
    if (orgId) return { kind: 'organization', id: orgId };
  }

  const contactId =
    (contact?.id != null && String(contact.id).trim()) || leadContactId || '';
  if (contactId) return { kind: 'contact', id: contactId };
  return null;
}

export type InvoicePartyRow = {
  party_name?: string | null;
  lead_id?: string | null;
  organization_id?: string | null;
};

/** Kunde: bei Eingangsrechnungen der Auftragskunde, sonst Rechnungsempfänger. */
export function invoiceCustomerColumnLabel(
  row: InvoicePartyRow,
  isIncoming: boolean,
  ctx: InvoicePartyDisplayContext,
): string {
  if (isIncoming) {
    const leadId = row.lead_id != null ? String(row.lead_id).trim() : '';
    if (!leadId) return '';
    return invoicePartyColumnLabel(null, leadId, ctx);
  }
  return invoicePartyColumnLabel(row.party_name, row.lead_id, ctx);
}

export function invoiceCustomerPartyTarget(
  row: InvoicePartyRow,
  isIncoming: boolean,
  ctx: InvoicePartyDisplayContext,
): InvoicePartyTarget | null {
  if (isIncoming) {
    const leadId = row.lead_id != null ? String(row.lead_id).trim() : '';
    if (!leadId) return null;
    return invoicePartyTarget(null, leadId, ctx);
  }
  return invoicePartyTarget(row.party_name, row.lead_id, ctx);
}

/** Eingangsrechnung mit Lieferantennamen aus dem Beleg, aber ohne zugeordnete Organisation. */
export function isUnassignedIncomingSupplier(row: InvoicePartyRow): boolean {
  return !String(row.organization_id ?? '').trim() && Boolean((row.party_name ?? '').trim());
}

/** Lieferant: nur bei Eingangsrechnungen (Organisation / gespeicherter Name). */
export function invoiceSupplierColumnLabel(
  row: InvoicePartyRow,
  isIncoming: boolean,
  ctx: InvoicePartyDisplayContext,
): string {
  if (!isIncoming) return '';
  return invoicePartyColumnLabel(row.party_name, null, ctx, row.organization_id);
}

export function invoiceSupplierPartyTarget(
  row: InvoicePartyRow,
  isIncoming: boolean,
  ctx: InvoicePartyDisplayContext,
): InvoicePartyTarget | null {
  if (!isIncoming) return null;
  return invoicePartyTarget(row.party_name, null, ctx, row.organization_id);
}
